<template>
  <v-card dark flat class="submission-card">
    <div class="submission-header">
      <span class="submission-icon"><v-icon>mdi-video-account</v-icon></span>
      <div>
        <div class="eyebrow">Evaluation</div>
        <h2>Video-Abgabe</h2>
        <p>{{ instructions }}</p>
      </div>
      <span v-if="submission" class="status-badge" :class="`status-${statusKey}`">
        <v-icon>{{ statusIcon }}</v-icon>{{ statusLabel }}
      </span>
    </div>

    <div v-if="submission" class="submission-state">
      <div>
        <small>Eingereicht</small>
        <strong>{{ formatDate(submission.submittedAt || submission.createdAt) }}</strong>
      </div>
      <div v-if="submission.reviewedAt">
        <small>Geprüft</small>
        <strong>{{ formatDate(submission.reviewedAt) }}</strong>
      </div>
    </div>

    <div v-if="submission && submission.feedback" class="feedback" :class="{ 'needs-revision': canReplace }">
      <v-icon>{{ canReplace ? "mdi-comment-alert-outline" : "mdi-comment-check-outline" }}</v-icon>
      <div><strong>Feedback der Academy-Leitung</strong><p>{{ submission.feedback }}</p></div>
    </div>

    <div v-if="!isLocked" class="upload-zone" :class="{ 'has-file': selectedFile }">
      <input ref="fileInput" type="file" accept="video/*" class="native-input" @change="handleFileSelect($event, 'gallery')" />
      <input ref="cameraInput" type="file" accept="video/*" capture="user" class="native-input" @change="handleFileSelect($event, 'camera')" />

      <template v-if="!selectedFile">
        <template v-if="selectingNativeVideo">
          <span class="upload-icon is-preparing"><v-icon>mdi-movie-cog-outline</v-icon></span>
          <h3>Video wird vorbereitet</h3>
          <p>Die bestehende App komprimiert das Galerie-Video und übergibt es anschliessend an den Upload. Dabei wird temporär zusätzlicher Arbeitsspeicher benötigt.</p>
          <v-progress-circular indeterminate color="#58d388" size="34" width="3" class="mt-4" />
        </template>
        <template v-else>
          <span class="upload-icon"><v-icon>mdi-cloud-upload-outline</v-icon></span>
          <h3>{{ canReplace ? "Überarbeitetes Video einreichen" : "Video aufnehmen oder auswählen" }}</h3>
          <p>
            MP4, MOV oder WebM bis maximal 250 MB. Galerie-Videos werden in der App mit der bestehenden Video-Optimierung vorbereitet.
            Kamera-Aufnahmen und Dateien im Browser bleiben unverändert; unter 100 MB kann nicht garantiert werden.
          </p>
          <div class="picker-actions">
            <v-btn color="#009640" depressed class="primary-action" @click="$refs.cameraInput.click()">
              <v-icon left>mdi-camera-outline</v-icon>Kamera
            </v-btn>
            <v-btn outlined color="#58d388" class="secondary-action" @click="openGalleryPicker">
              <v-icon left>mdi-folder-video-outline</v-icon>Datei wählen
            </v-btn>
          </div>
          <div v-if="error" class="upload-error selection-error"><v-icon>mdi-alert-circle-outline</v-icon><span>{{ error }}</span></div>
        </template>
      </template>

      <template v-else>
        <div class="file-row">
          <span><v-icon>mdi-file-video-outline</v-icon></span>
          <div><strong>{{ selectedFile.name }}</strong><small>{{ formatSize(selectedFile.size) }} · {{ totalChunks }} Teile</small></div>
          <v-btn
            v-if="!uploading && !completing && !preparingUpload"
            icon
            aria-label="Datei und Upload entfernen"
            :loading="discarding"
            :disabled="discarding"
            @click="discardUpload">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div v-if="optimizationNote" class="optimization-note" :class="{ warning: selectedFile.size > browserNoticeSize }">
          <v-icon>{{ selectedFile.size > browserNoticeSize ? "mdi-information-outline" : "mdi-check-circle-outline" }}</v-icon>
          <span>{{ optimizationNote }}</span>
        </div>

        <div v-if="uploading || completing || uploadProgress > 0" class="upload-progress">
          <div><span>{{ uploadMessage }}</span><strong>{{ Math.round(uploadProgress) }}%</strong></div>
          <v-progress-linear :value="uploadProgress" color="#009640" background-color="rgba(255,255,255,0.08)" height="8" rounded />
        </div>

        <div v-if="error" class="upload-error"><v-icon>mdi-alert-circle-outline</v-icon><span>{{ error }}</span></div>
        <div v-if="blockingUploadId" class="upload-note">
          <v-icon>mdi-file-alert-outline</v-icon>
          <span>Der bestehende Upload gehört zu einer anderen Datei. Verwirf ihn zuerst; die neu gewählte Datei wird niemals darin fortgesetzt.</span>
        </div>

        <div class="upload-actions">
          <div v-if="fingerprinting" class="preparation-state">
            <v-progress-circular indeterminate color="#58d388" size="22" width="2" />
            <span>Die Datei wird lokal geprüft. Dabei werden nur kleine Ausschnitte gelesen und noch keine Daten hochgeladen.</span>
          </div>
          <div v-else-if="startingUpload" class="preparation-state">
            <v-progress-circular indeterminate color="#58d388" size="22" width="2" />
            <span>Der sichere Upload wird vorbereitet.</span>
          </div>
          <div v-else-if="completing" class="completion-state">
            <v-icon>mdi-shield-check-outline</v-icon>
            <span>Das Video wird jetzt sicher zusammengesetzt und eingereicht. Dieser letzte Schritt kann nicht mehr abgebrochen werden.</span>
          </div>
          <v-btn
            v-else-if="uploading"
            outlined
            color="#ff7b86"
            class="secondary-action"
            :loading="discarding"
            :disabled="discarding"
            @click="cancelUpload">
            <v-icon left>mdi-close-circle-outline</v-icon>Abbrechen
          </v-btn>
          <v-btn
            v-else-if="blockingUploadId"
            outlined
            color="#ffb86b"
            class="secondary-action"
            :loading="discarding"
            :disabled="discarding"
            @click="discardBlockingUpload(false)">
            <v-icon left>mdi-delete-clock-outline</v-icon>Bestehenden Upload verwerfen
          </v-btn>
          <v-btn v-else-if="error" color="#009640" depressed class="primary-action" :disabled="discarding" @click="resumeUpload">
            <v-icon left>mdi-reload</v-icon>Upload fortsetzen
          </v-btn>
          <v-btn v-else color="#009640" depressed class="primary-action" :disabled="discarding" @click="startUpload">
            <v-icon left>mdi-send-outline</v-icon>Video einreichen
          </v-btn>
        </div>
      </template>
    </div>

    <div v-else class="locked-note">
      <v-icon>{{ ["approved", "passed"].includes(statusKey) ? "mdi-check-decagram-outline" : "mdi-clock-check-outline" }}</v-icon>
      <span>{{ lockedMessage }}</span>
    </div>
  </v-card>
</template>

<script>
import api from "@/api"

const CHUNK_SIZE = 10 * 1024 * 1024
const BROWSER_NOTICE_SIZE = 100 * 1024 * 1024
const MAX_FILE_SIZE = 250 * 1024 * 1024
const MAX_CHUNK_RETRIES = 3
const FINGERPRINT_SAMPLE_SIZE = 1024 * 1024
const FINGERPRINT_PREFIX = "roody-leadership-video-fingerprint-v1\0"

function readBlobBuffer(blob) {
  if (typeof blob.arrayBuffer === "function") return blob.arrayBuffer()
  return new Promise((resolve, reject) => {
    if (typeof FileReader === "undefined") {
      reject(new Error("Dieser Browser kann die Videodatei nicht sicher prüfen."))
      return
    }
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error || new Error("Die Videodatei konnte nicht gelesen werden."))
    reader.readAsArrayBuffer(blob)
  })
}

function estimateBase64Size(value) {
  if (typeof value !== "string" || !value.length) return 0
  const padding = value.endsWith("==") ? 2 : value.endsWith("=") ? 1 : 0
  return Math.max(0, Math.floor((value.length * 3) / 4) - padding)
}

function createFileFromBase64(value, fileName, mimeType) {
  if (typeof atob !== "function") throw new Error("Dieser Browser kann das vorbereitete Video nicht lesen.")
  const partSize = 4 * 1024 * 1024
  const parts = []
  for (let offset = 0; offset < value.length; offset += partSize) {
    const binary = atob(value.slice(offset, Math.min(offset + partSize, value.length)))
    const bytes = new Uint8Array(binary.length)
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index)
    parts.push(bytes)
  }
  return new File(parts, fileName, { type: mimeType, lastModified: Date.now() })
}

function getPreparedVideoName(fileName, mimeType) {
  const originalName = String(fileName || "leadership-video")
  if (mimeType !== "video/mp4") return originalName
  return `${originalName.replace(/\.[^/.]+$/, "") || "leadership-video"}_optimiert.mp4`
}

async function createFileFingerprint(file) {
  const browserCrypto = typeof window !== "undefined" ? window.crypto : null
  if (!browserCrypto || !browserCrypto.subtle) {
    throw new Error("Dieser Browser unterstützt die sichere Dateiprüfung nicht. Der Upload wurde nicht gestartet.")
  }
  if (typeof TextEncoder === "undefined" || typeof file.name.normalize !== "function") {
    throw new Error("Dieser Browser kann keinen stabilen Datei-Fingerabdruck erstellen. Der Upload wurde nicht gestartet.")
  }

  const encoder = new TextEncoder()
  const metadata = encoder.encode(
    JSON.stringify({
      fileName: file.name.normalize("NFC"),
      mimeType: String(file.type || "").trim().toLowerCase(),
      size: Number(file.size),
      lastModified: Number(file.lastModified || 0),
    }),
  )
  const prefix = encoder.encode(FINGERPRINT_PREFIX)
  const headEnd = Math.min(file.size, FINGERPRINT_SAMPLE_SIZE)
  const tailStart = Math.max(0, file.size - FINGERPRINT_SAMPLE_SIZE)
  const [headBuffer, tailBuffer] = await Promise.all([readBlobBuffer(file.slice(0, headEnd)), readBlobBuffer(file.slice(tailStart, file.size))])
  const head = new Uint8Array(headBuffer)
  const tail = new Uint8Array(tailBuffer)
  const framed = new Uint8Array(prefix.length + 12 + metadata.length + head.length + tail.length)
  const frameView = new DataView(framed.buffer)
  let offset = 0
  framed.set(prefix, offset)
  offset += prefix.length
  frameView.setUint32(offset, metadata.length, false)
  offset += 4
  framed.set(metadata, offset)
  offset += metadata.length
  frameView.setUint32(offset, head.length, false)
  offset += 4
  framed.set(head, offset)
  offset += head.length
  frameView.setUint32(offset, tail.length, false)
  offset += 4
  framed.set(tail, offset)

  const digest = await browserCrypto.subtle.digest("SHA-256", framed)
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("")
}

export default {
  name: "LeadershipVideoSubmission",
  props: {
    moduleId: { type: String, required: true },
    instructions: {
      type: String,
      default: "Nimm ein kurzes Video auf und beschreibe, wie du das Gelernte in deinem Führungsalltag anwendest.",
    },
  },
  data() {
    return {
      submission: null,
      selectedFile: null,
      fileFingerprint: "",
      fingerprinting: false,
      startingUpload: false,
      uploading: false,
      completing: false,
      uploadProgress: 0,
      uploadId: null,
      blockingUploadId: null,
      currentChunk: 0,
      receivedChunks: [],
      retryAttempt: 0,
      error: "",
      abortController: null,
      discarding: false,
      selectingNativeVideo: false,
      optimizationNote: "",
    }
  },
  computed: {
    preparingUpload() {
      return this.fingerprinting || this.startingUpload
    },
    browserNoticeSize() {
      return BROWSER_NOTICE_SIZE
    },
    statusKey() {
      return (this.submission && this.submission.status) || "submitted"
    },
    statusLabel() {
      const labels = {
        submitted: "Eingereicht",
        pending: "Eingereicht",
        in_review: "In Prüfung",
        reviewing: "In Prüfung",
        approved: "Bestanden",
        passed: "Bestanden",
        changes_requested: "Überarbeitung",
        revision_requested: "Überarbeitung",
        revision_required: "Überarbeitung",
        rejected: "Überarbeitung",
      }
      return labels[this.statusKey] || "Eingereicht"
    },
    statusIcon() {
      if (["approved", "passed"].includes(this.statusKey)) return "mdi-check-circle-outline"
      if (["changes_requested", "revision_requested", "revision_required", "rejected"].includes(this.statusKey)) return "mdi-reload"
      return "mdi-clock-outline"
    },
    canReplace() {
      return ["changes_requested", "revision_requested", "revision_required", "rejected"].includes(this.statusKey)
    },
    isLocked() {
      return Boolean(this.submission && !this.canReplace)
    },
    totalChunks() {
      return this.selectedFile ? Math.ceil(this.selectedFile.size / CHUNK_SIZE) : 0
    },
    uploadMessage() {
      if (this.completing) return "Video wird verarbeitet und eingereicht"
      if (this.error && this.uploadProgress >= 100) return "Upload bereit zum erneuten Abschluss"
      if (!this.uploading && this.uploadProgress >= 100) return "Upload abgeschlossen"
      if (this.retryAttempt > 0) return `Teil ${this.currentChunk + 1} wird erneut versucht (${this.retryAttempt}/${MAX_CHUNK_RETRIES})`
      return `Teil ${Math.min(this.currentChunk + 1, this.totalChunks)} von ${this.totalChunks}`
    },
    lockedMessage() {
      return ["approved", "passed"].includes(this.statusKey)
        ? "Deine Video-Evaluation ist bestanden und das Modul ist abgeschlossen."
        : "Deine Abgabe ist eingereicht. Du wirst benachrichtigt, sobald Feedback vorliegt."
    },
  },
  created() {
    this.uploadDisposed = false
    this.uploadTask = null
  },
  mounted() {
    this.loadSubmission()
  },
  beforeDestroy() {
    this.uploadDisposed = true
    if (this.abortController) this.abortController.abort()
  },
  methods: {
    async loadSubmission() {
      try {
        const result = await api.akademie.fetchLeadershipSubmission(this.moduleId)
        this.submission = result && (result.submission || (Array.isArray(result) ? result[0] : result))
      } catch (error) {
        if (!error.response || error.response.status !== 404) console.error("Video-Abgabe konnte nicht geladen werden", error)
      }
    },
    getFlutterHandler() {
      if (typeof window === "undefined") return null
      const handler = window.flutter_inappwebview
      return handler && typeof handler.callHandler === "function" ? handler : null
    },
    async openGalleryPicker() {
      if (this.selectingNativeVideo || this.preparingUpload || this.uploading || this.completing || this.discarding) return
      const handler = this.getFlutterHandler()
      if (!handler) {
        if (this.$refs.fileInput) this.$refs.fileInput.click()
        return
      }
      await this.pickNativeGalleryVideo(handler)
    },
    async pickNativeGalleryVideo(handler) {
      this.selectingNativeVideo = true
      this.optimizationNote = ""
      this.error = ""

      let result = null
      let base64 = ""
      try {
        result = await handler.callHandler("pickAndCompressMedia")
        if (this.uploadDisposed || !result || !result.success) {
          if (result && result.error) this.error = `Das Video konnte nicht vorbereitet werden: ${result.error}`
          return
        }

        const mimeType = String(result.mimeType || "").toLowerCase()
        if (!mimeType.startsWith("video/")) {
          this.error = "Bitte wähle in der Galerie eine Videodatei aus."
          return
        }

        base64 = typeof result.base64 === "string" ? result.base64 : ""
        const estimatedSize = estimateBase64Size(base64)
        if (!estimatedSize) {
          this.error = "Das vorbereitete Video ist leer oder konnte nicht gelesen werden."
          return
        }
        if (estimatedSize > MAX_FILE_SIZE) {
          this.error = "Das Video ist auch nach der App-Optimierung grösser als 250 MB. Bitte kürze es und versuche es erneut."
          return
        }

        const fileName = getPreparedVideoName(result.name, mimeType)
        try {
          result.base64 = ""
        } catch (_) {
          // Manche WebView-Versionen liefern ein nicht beschreibbares Resultatobjekt.
        }
        const file = createFileFromBase64(base64, fileName, mimeType)
        base64 = ""
        if (file.size > MAX_FILE_SIZE) {
          this.error = "Das Video ist auch nach der App-Optimierung grösser als 250 MB. Bitte kürze es und versuche es erneut."
          return
        }

        const note =
          file.size > BROWSER_NOTICE_SIZE
            ? "Die bestehende App hat das Galerie-Video komprimiert, es bleibt aber über 100 MB. Bis 250 MB kann es hochgeladen werden; bei der Vorbereitung wird temporär zusätzlicher Arbeitsspeicher verwendet."
            : "Die bestehende App hat das Galerie-Video vor dem Upload komprimiert. Bei der Vorbereitung wird temporär zusätzlicher Arbeitsspeicher verwendet."
        this.applySelectedFile(file, note)
      } catch (error) {
        this.error = error.message || "Die Videoauswahl der App konnte nicht abgeschlossen werden."
      } finally {
        base64 = ""
        try {
          if (result && result.base64) result.base64 = ""
        } catch (_) {
          // Der lokale String wird trotzdem freigegeben, sobald der Handler endet.
        }
        this.selectingNativeVideo = false
      }
    },
    applySelectedFile(file, note) {
      this.selectedFile = file
      this.optimizationNote = note || ""
      this.fileFingerprint = ""
      this.uploadId = null
      this.blockingUploadId = null
      this.currentChunk = 0
      this.receivedChunks = []
      this.retryAttempt = 0
      this.uploadProgress = 0
      this.error = ""
    },
    handleFileSelect(event, source = "gallery") {
      const file = event.target.files && event.target.files[0]
      event.target.value = ""
      if (!file) return
      this.error = ""
      if (!file.type || !file.type.startsWith("video/")) {
        this.error = "Bitte wähle eine Videodatei aus."
        return
      }
      if (file.size > MAX_FILE_SIZE) {
        this.error = "Das Video ist grösser als 250 MB. Bitte kürze oder komprimiere es."
        return
      }
      const isAppCamera = source === "camera" && Boolean(this.getFlutterHandler())
      const note = isAppCamera
        ? file.size > BROWSER_NOTICE_SIZE
          ? "Die Kamera-Aufnahme wird von der bestehenden App unverändert verwendet und ist über 100 MB. Bis maximal 250 MB kann sie hochgeladen werden."
          : "Die Kamera-Aufnahme liegt unter 100 MB und wird von der bestehenden App unverändert verwendet."
        : file.size > BROWSER_NOTICE_SIZE
          ? "Dieser Browser bietet keine verlässliche lokale Videokompression. Die Originaldatei wird hochgeladen; für kleinere Dateien kannst du das Video vorher im Betriebssystem exportieren oder die App verwenden."
          : "Das Video liegt unter 100 MB. Im Browser wird die Originaldatei unverändert verwendet."
      this.applySelectedFile(file, note)
    },
    async startUpload() {
      if (!this.selectedFile || this.preparingUpload || this.uploading || this.completing || this.blockingUploadId) return
      const selectedFile = this.selectedFile
      this.error = ""
      this.fingerprinting = true
      try {
        this.fileFingerprint = await createFileFingerprint(selectedFile)
      } catch (error) {
        this.error = error.message || "Der Datei-Fingerabdruck konnte nicht erstellt werden. Der Upload wurde nicht gestartet."
        return
      } finally {
        this.fingerprinting = false
      }
      if (this.uploadDisposed || this.selectedFile !== selectedFile || !this.fileFingerprint) return

      this.startingUpload = true
      try {
        const session = await api.akademie.startLeadershipSubmissionUpload(this.moduleId, {
          fileName: selectedFile.name,
          mimeType: selectedFile.type,
          fileSize: selectedFile.size,
          fileFingerprint: this.fileFingerprint,
          totalChunks: this.totalChunks,
          chunkSize: CHUNK_SIZE,
        })
        if (this.uploadDisposed) return
        this.uploadId = session.uploadId || session._id
        this.blockingUploadId = null
        this.receivedChunks = Array.isArray(session.receivedChunks) ? session.receivedChunks.map(Number) : []
        this.currentChunk = this.firstMissingChunk()
        this.uploadProgress = this.totalChunks ? (this.receivedChunks.length / this.totalChunks) * 100 : 0
        if (!this.uploadId) throw new Error("Der Upload konnte nicht vorbereitet werden.")
        this.startingUpload = false
        await this.runUpload()
      } catch (error) {
        this.handleUploadError(error)
      } finally {
        this.startingUpload = false
      }
    },
    async resumeUpload() {
      if (this.blockingUploadId || this.preparingUpload || this.completing) return
      if (!this.uploadId) return this.startUpload()
      await this.runUpload()
    },
    async runUpload() {
      const task = this.uploadChunks()
      this.uploadTask = task
      try {
        await task
      } finally {
        if (this.uploadTask === task) this.uploadTask = null
      }
    },
    async uploadChunks() {
      this.uploading = true
      this.completing = false
      this.error = ""
      this.abortController = new AbortController()
      try {
        for (let index = this.currentChunk; index < this.totalChunks; index += 1) {
          if (this.receivedChunks.includes(index)) continue
          this.currentChunk = index
          const start = index * CHUNK_SIZE
          const chunk = this.selectedFile.slice(start, Math.min(start + CHUNK_SIZE, this.selectedFile.size))
          await this.uploadChunkWithRetry(index, chunk)
          this.receivedChunks.push(index)
          this.uploadProgress = (this.receivedChunks.length / this.totalChunks) * 100
        }
        this.completing = true
        const submission = await api.akademie.completeLeadershipSubmissionUpload(this.moduleId, this.uploadId)
        this.submission = submission.submission || submission
        this.$emit("submitted", this.submission)
        this.selectedFile = null
        this.optimizationNote = ""
        this.uploadId = null
        this.currentChunk = 0
        this.receivedChunks = []
      } catch (error) {
        if (!this.isUploadCanceled(error)) this.handleUploadError(error)
      } finally {
        this.completing = false
        this.uploading = false
        this.abortController = null
      }
    },
    async uploadChunkWithRetry(index, chunk) {
      let lastError = null
      for (let attempt = 0; attempt < MAX_CHUNK_RETRIES; attempt += 1) {
        this.retryAttempt = attempt
        try {
          await api.akademie.uploadLeadershipSubmissionChunk(this.moduleId, this.uploadId, index, chunk, {
            signal: this.abortController.signal,
            onProgress: (event) => {
              const loadedRatio = event.total ? event.loaded / event.total : 0
              this.uploadProgress = ((this.receivedChunks.length + loadedRatio) / this.totalChunks) * 100
            },
          })
          this.retryAttempt = 0
          return
        } catch (error) {
          lastError = error
          if (this.isUploadCanceled(error)) throw error
          if (attempt < MAX_CHUNK_RETRIES - 1) await new Promise((resolve) => setTimeout(resolve, 600 * (attempt + 1)))
        }
      }
      this.retryAttempt = 0
      throw lastError
    },
    async cancelUpload() {
      if (this.preparingUpload || this.completing) return
      await this.discardUpload()
    },
    async discardUpload() {
      if (this.discarding || this.preparingUpload || this.completing) return
      if (this.blockingUploadId) {
        await this.discardBlockingUpload(true)
        return
      }
      this.discarding = true
      const activeTask = this.uploadTask
      if (this.abortController) this.abortController.abort()
      const uploadId = this.uploadId
      try {
        if (activeTask) await activeTask
        if (uploadId) {
          await api.akademie.cancelLeadershipSubmissionUpload(this.moduleId, uploadId)
        }
      } catch (_) {
        // Best effort: the server also expires unfinished upload sessions automatically.
      } finally {
        this.resetUpload()
        this.discarding = false
      }
    },
    async discardBlockingUpload(resetSelection = false) {
      if (!this.blockingUploadId || this.discarding || this.preparingUpload || this.uploading || this.completing) return
      const uploadId = this.blockingUploadId
      this.discarding = true
      let removed = false
      try {
        await api.akademie.cancelLeadershipSubmissionUpload(this.moduleId, uploadId)
        removed = true
      } catch (error) {
        const status = error.response && error.response.status
        if ([404, 410].includes(status)) {
          removed = true
        } else {
          this.error =
            (error.response && error.response.data && (error.response.data.error || error.response.data.message)) ||
            "Der bestehende Upload konnte nicht verworfen werden. Bitte versuche es erneut."
        }
      } finally {
        if (removed) {
          this.blockingUploadId = null
          if (resetSelection) {
            this.resetUpload()
          } else {
            this.uploadId = null
            this.currentChunk = 0
            this.receivedChunks = []
            this.retryAttempt = 0
            this.uploadProgress = 0
            this.error = ""
          }
        }
        this.discarding = false
      }
    },
    resetUpload() {
      this.selectedFile = null
      this.fileFingerprint = ""
      this.fingerprinting = false
      this.startingUpload = false
      this.uploadId = null
      this.blockingUploadId = null
      this.completing = false
      this.currentChunk = 0
      this.receivedChunks = []
      this.retryAttempt = 0
      this.uploadProgress = 0
      this.error = ""
      this.optimizationNote = ""
    },
    handleUploadError(error) {
      const responseData = error.response && error.response.data
      const existing = responseData && responseData.submission
      if (existing) {
        this.submission = existing
        this.resetUpload()
        return
      }
      if (responseData && responseData.uploadId && !this.uploadId) {
        this.blockingUploadId = responseData.uploadId
      }
      this.error =
        (responseData && (responseData.error || responseData.message)) ||
        error.message ||
        "Der Upload wurde unterbrochen. Du kannst ihn fortsetzen."
      this.uploading = false
    },
    isUploadCanceled(error) {
      return Boolean(
        (this.abortController && this.abortController.signal.aborted) ||
          (error && (error.__CANCEL__ || error.name === "AbortError" || error.name === "CanceledError" || error.code === "ERR_CANCELED")),
      )
    },
    firstMissingChunk() {
      for (let index = 0; index < this.totalChunks; index += 1) {
        if (!this.receivedChunks.includes(index)) return index
      }
      return this.totalChunks
    },
    formatSize(bytes) {
      if (!bytes) return "0 MB"
      return `${(bytes / 1024 / 1024).toFixed(1)} MB`
    },
    formatDate(date) {
      if (!date) return "-"
      return new Date(date).toLocaleString("de-CH", { dateStyle: "medium", timeStyle: "short" })
    },
  },
}
</script>

<style scoped>
.submission-card {
  margin-top: 1rem;
  padding: 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1.25rem !important;
  color: #f4f4f5;
  background: radial-gradient(circle at 100% 0, rgba(147, 51, 234, 0.1), transparent 15rem), rgba(255, 255, 255, 0.035) !important;
  font-size: 1rem;
}

.submission-header {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: start;
  gap: 0.875rem;
}

.submission-icon,
.upload-icon {
  display: grid;
  place-items: center;
  border-radius: 0.9375rem;
  color: #c084fc;
  background: rgba(147, 51, 234, 0.14);
}

.submission-icon {
  width: 3rem;
  height: 3rem;
}

.submission-icon .v-icon,
.upload-icon .v-icon {
  color: currentColor;
}

.eyebrow {
  color: #c084fc;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.submission-header h2 {
  margin: 0.1875rem 0 0.375rem;
  color: #fff;
  font-size: 1.25rem;
}

.submission-header p,
.upload-zone > p {
  margin: 0;
  color: #b8b8bd;
  font-size: 0.875rem;
  line-height: 1.55;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  min-height: 2rem;
  padding: 0.375rem 0.625rem;
  border-radius: 999px;
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.14);
  font-size: 0.75rem;
  font-weight: 750;
}

.status-badge .v-icon {
  color: currentColor;
  font-size: 1rem;
}

.status-approved,
.status-passed {
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.14);
}

.status-changes_requested,
.status-revision_requested,
.status-revision_required,
.status-rejected {
  color: #ff9aa4;
  background: rgba(244, 63, 94, 0.14);
}

.submission-state {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1rem;
}

.submission-state > div {
  display: grid;
  gap: 0.125rem;
  min-width: 10rem;
  padding: 0.75rem;
  border-radius: 0.875rem;
  background: rgba(255, 255, 255, 0.04);
}

.submission-state small,
.file-row small {
  color: #a3a3a8;
  font-size: 0.75rem;
}

.submission-state strong,
.file-row strong {
  color: #fff;
  font-size: 0.875rem;
}

.feedback,
.upload-error,
.upload-note,
.preparation-state,
.completion-state,
.locked-note {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  margin-top: 1rem;
  padding: 0.875rem;
  border-radius: 0.875rem;
}

.feedback {
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.11);
}

.feedback.needs-revision,
.upload-error {
  color: #ff9aa4;
  background: rgba(244, 63, 94, 0.1);
}

.selection-error {
  width: min(100%, 40rem);
  text-align: left;
}

.upload-note {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.1);
  font-size: 0.875rem;
  line-height: 1.45;
}

.completion-state {
  max-width: 40rem;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.11);
  font-size: 0.875rem;
  line-height: 1.45;
}

.preparation-state {
  max-width: 40rem;
  color: #d4d4d8;
  background: rgba(255, 255, 255, 0.05);
  font-size: 0.875rem;
  line-height: 1.45;
}

.feedback .v-icon,
.upload-error .v-icon,
.upload-note .v-icon,
.preparation-state .v-icon,
.completion-state .v-icon,
.locked-note .v-icon {
  color: currentColor;
}

.feedback strong {
  color: currentColor;
  font-size: 0.875rem;
}

.feedback p {
  margin: 0.25rem 0 0;
  color: #d4d4d8;
  font-size: 0.875rem;
  line-height: 1.5;
}

.upload-zone {
  display: grid;
  justify-items: center;
  margin-top: 1rem;
  padding: 1.25rem;
  border: 1px dashed rgba(255, 255, 255, 0.16);
  border-radius: 1rem;
  text-align: center;
}

.upload-zone.has-file {
  justify-items: stretch;
  text-align: left;
}

.native-input {
  display: none;
}

.upload-icon {
  width: 3.5rem;
  height: 3.5rem;
}

.upload-icon .v-icon {
  font-size: 1.75rem;
}

.upload-icon.is-preparing {
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.14);
}

.upload-zone h3 {
  margin: 0.875rem 0 0.375rem;
  color: #fff;
  font-size: 1.125rem;
}

.picker-actions,
.upload-actions {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 1rem;
}

.primary-action,
.secondary-action {
  min-height: 2.75rem !important;
  border-radius: 0.875rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.file-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
}

.file-row > span {
  display: grid;
  width: 2.75rem;
  height: 2.75rem;
  place-items: center;
  border-radius: 0.875rem;
  color: #c084fc;
  background: rgba(147, 51, 234, 0.13);
}

.file-row > span .v-icon {
  color: currentColor;
}

.file-row > div {
  display: grid;
  min-width: 0;
}

.file-row strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.upload-progress {
  margin-top: 1rem;
}

.optimization-note {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  margin-top: 1rem;
  padding: 0.75rem 0.875rem;
  border-radius: 0.875rem;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.1);
  font-size: 0.875rem;
  line-height: 1.45;
}

.optimization-note.warning {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.1);
}

.optimization-note .v-icon {
  color: currentColor;
}

.upload-progress > div {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.375rem;
  color: #b8b8bd;
  font-size: 0.75rem;
}

.upload-progress strong {
  color: #7ee5a6;
}

.upload-error {
  font-size: 0.875rem;
  line-height: 1.45;
}

.locked-note {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.1);
  font-size: 0.875rem;
  line-height: 1.45;
}

@media (max-width: 43rem) {
  .submission-card {
    padding: 1rem;
  }

  .submission-header {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .status-badge {
    grid-column: 1 / -1;
    justify-self: start;
  }

  .picker-actions,
  .upload-actions {
    width: 100%;
    flex-direction: column;
  }
}
</style>
